import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Input, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Inbox as InboxIcon, Lock } from 'lucide-react';
import type { FillState, InboxGroupDto, Paginated, PolicyDto } from '@pams/shared';
import { FILL_STATES, INBOX_SORT_FIELDS, PAGINATION, readableDate } from '@pams/shared';
import { DatePicker } from '@/components/DatePicker';
import { asPickerDate } from '@/lib/pickerDate';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { AddresseeCodes } from '@/components/AddresseeCodes';
import { RaiserPill } from '@/components/RaiserPill';
import { ArrangementPreviewModal, PreviewEye } from '@/components/preview/ArrangementPreviewModal';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { OrgUnitPicker } from '@/components/OrgUnitPicker';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import { api } from '@/lib/api';
import { ARRANGEMENT_SEARCH_FIELD_CLASS, searchTermOf } from '@/lib/arrangementSearch';
import { fillBadge, fillFilterLabel, namedOf } from '@/lib/statusPresentation';
import { queryKeys } from '@/lib/queryKeys';
import { inboxTreeRows, type InboxTreeRow } from '@/lib/inboxTree';
import { HierarchyTable } from '@/components/HierarchyTable';
import { useTreeExpansion } from '@/hooks/useTreeExpansion';
import { INBOX_COPY, truncateName } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE NODAL INBOX — FR-5.3.1, and `useServerTable`'s TENTH consumer with no hook change.
 *
 *  ⚠️ ONE ROW PER ADDRESSED SHARE, NOT PER ORDER. A three-day recurring order is three
 *  rows, because completion is per division per duty (FR-5.3.6b) and "8 of 12 named" is a
 *  statement about ONE night. One row per arrangement would show a division that finished
 *  Monday and has not started Wednesday a single amber line that is true of neither.
 *
 *  ⚠️ "RED FIRST" IS THE SERVER'S ORDER, NOT A RE-SORT OF ONE PAGE. `fill_rank` is a stored
 *  number with an index behind it; sorting a page on the client would order twenty rows and
 *  lie about the rest.
 *
 *  ⚠️ AND "TODAY" IS ALSO THE SERVER'S. Writing today's date into the URL on mount would
 *  make the back button strange, bake a date into every shared link, and open a link shared
 *  at 23:59 on yesterday.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [C5c §7a] THE PAGE THE TABLE HOLDS, TAGGED WITH WHETHER A SEARCH PRODUCED IT — `useServerTable`'s
 * `TPage`, `pages/Arrangements`' `searchedFor` idiom. `keepPreviousData` keeps this page on screen
 * while the next request is in flight, so the empty state must read what produced THIS page, never
 * the box, which is already the next term.
 */
type InboxTablePage = Paginated<InboxTreeRow> & { readonly searched?: true };

const InboxPage = () => {
  const navigate = useNavigate();
  const table = useServerTable<InboxTreeRow, (typeof INBOX_SORT_FIELDS)[number], InboxTablePage>({
    sortFields: INBOX_SORT_FIELDS,
    /*
     * ⚠️ [amended C1c §3] `dutyDate`, WHICH IS THE SERVER'S OWN DEFAULT — A DRIFT, CLOSED.
     *
     * This said `fillState` while `listInboxQuerySchema` has defaulted to `dutyDate` since
     * [D4.5 §3a], "because over eight days an operator works through nights rather than through
     * colours". `useServerTable` writes its own default into the request, so the server's never
     * applied and the documented decision had no effect for two passes.
     *
     * ⚠️ IT ALSO DECIDES HOW WELL §3's BAND READS. `inboxSort`'s `TIE_BREAK` puts
     * `arrangementId` second, so under the date sort one order's rows are already adjacent —
     * and under `fillState` they are scattered across the colour bands.
     */
    defaultSortBy: 'dutyDate',
    defaultSortOrder: 'asc',
    defaultLimit: 20,
    /*
     * ⚠️ [C1d §2] `show` IS THE ARCHIVE AXIS, AND IT IS THE SAME ONE AS EVERYWHERE ELSE.
     * `listInboxQuerySchema` declares it too — the middleware STRIPS unknown keys, so a
     * control wired only here would send `?show=archived` and get an unchanged list back with
     * no error, no 422 and no log line ([D4.4]'s named defect).
     */
    filterKeys: ['dateFrom', 'dateTo', 'fillState', 'divisionOrgUnitId', 'policyId', 'show'],
    queryKey: (params) => queryKeys.inbox.list(params),
    /*
     * ⚠️ [C3c §2] THE TREE IS ASSEMBLED HERE, INSIDE `queryFn`, so `useServerTable` is
     * instantiated on the RENDERED row type and `tableProps` spreads with no cast. `total` and
     * `pages` stay the SERVER's — they count ARRANGEMENTS (§2d) — rather than a length taken
     * from a flattened array here.
     */
    queryFn: async (params, signal) => {
      /*
       * ⚠️ [C5c §7a] THE SEARCH RIDES THE HOOK'S OWN `search`, SO IT SHARES ITS ONE DEBOUNCE ([C3c §14b])
       * and `filterKeys` does not change. TRIMMED, AND NOT SENT WHEN BLANK — `listInboxQuerySchema`'s
       * `.trim().min(1)` would answer a space with a 422 on the landing screen.
       */
      const search = searchTermOf(params.search);
      const page = await api.get<Paginated<InboxGroupDto>>('/inbox', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          ...(search ? { search } : {}),
          ...params.filters,
        },
        signal,
      });
      return { ...page, items: inboxTreeRows(page.items), ...(search ? { searched: true as const } : {}) };
    },
  });

  /*
   * ⚠️ [C1d §2] `|| 'current'` — `useServerTable` STRIPS an empty filter value so it never
   * reaches the URL, which keeps a shared link clean; the page re-defaults on the way out,
   * exactly as `pages/Arrangements` does for the same control.
   */
  const show = table.params.filters.show || 'current';

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended C3c.1] A FILTERED HEADER STATES NEITHER A COUNT, NOR A STATUS, NOR A DUTY SPAN
   *  — AND §2 SHIPPED STATING ALL THREE.
   *
   *  CLAUDE.md §8 [C3c §2] records the rule — "WITH A `fillState` FILTER ACTIVE THE HEADER'S
   *  COUNT, STATUS AND DATE SPAN ARE SUPPRESSED… a number that is right about a different set
   *  is worse than no number" — and the columns rendered them unconditionally.
   *
   *  ⚠️ THE DATE SPAN IS THE THIRD, AND THE FIRST FIX MISSED IT. `dutyDateFrom`/`dutyDateTo` are
   *  `$min`/`$max` over the SAME post-filter `$group` as the sums, so they have exactly the
   *  property the fix declares unacceptable: an order running 15–21 Aug with one unnamed row on
   *  the 21st reads "21 Aug 2026" alone under Status = Not named — a header stating an order's
   *  duty window from a one-row subset, on the same row where two neighbouring cells were
   *  deliberately blanked for that reason. Suppressing two of three is the worse outcome, because
   *  the dashes beside it are what tell an operator the row was narrowed.
   *
   *  ⚠️ IT IS NOT COSMETIC, BECAUSE THE `$match` ON `rank` RUNS **BETWEEN** THE TWO `$group`s.
   *  The header's sums therefore describe only the children that survived the filter: an order
   *  with four fully-named duty rows and one untouched one reads "40 of 42 · amber" unfiltered
   *  and "0 of 2 · RED" under Status = Not named — a header asserting that nobody on the order
   *  has been named, about an order that is almost entirely staffed.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const fillStateFiltered = (table.params.filters.fillState ?? '') !== '';

  /*
   * The policy list is a bounded master read, so it rides the existing `policies.list` key
   * rather than a new one — `useServerTable`'s consumers already keep it warm.
   */
  /* ⚠️ [C1e §2f] ONE modal for the whole page, never one per row — [PRE-P6.1] measured what
     an overlay per list row costs (64s → 119s on one test file). */
  const [preview, setPreview] = useState<{ id: string; eyeKey: string } | null>(null);

  const policies = useQuery({
    queryKey: queryKeys.policies.list({ page: 1, limit: PAGINATION.MAX_LIMIT }),
    queryFn: ({ signal }) =>
      api.get<Paginated<PolicyDto>>('/policies', { query: { page: 1, limit: PAGINATION.MAX_LIMIT }, signal }),
    staleTime: 5 * 60_000,
  });

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3c §2a/§2b] ONE COLUMN ARRAY FOR BOTH ROW KINDS, AND THE `Duty Point` COLUMN IS GONE.
   *
   *  `HierarchyTable` renders every data column for a header and for a duty row alike, so each
   *  render branches on `rowKind`. §2a deletes the duty-point COLUMN; the fact moves into the
   *  tree cell, where `DP1 · Esplanade` is what §2b asks for.
   *
   *  ⚠️ THE TIME COLUMN LOSES ITS SORTER. The time now renders on a CHILD row, and a header that
   *  claims to sort rows it does not order is a lie — the pager orders arrangements. Date and
   *  Status keep theirs, because those are the group's own columns.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const allColumns: ColumnsType<InboxTreeRow> = [
    {
      title: INBOX_COPY.columns.addressee,
      key: 'addressee',
      render: (_value, row) =>
        row.rowKind === 'duty' ? <AddresseeCodes division={row.division} unit={row.addressees} /> : null,
    },
    {
      title: INBOX_COPY.columns.date,
      key: 'dutyDate',
      sorter: true,
      render: (_value, row) => (
        <span className="whitespace-nowrap">
          {row.rowKind === 'duty' ? (
            (readableDate(row.dutyDate) ?? row.dutyDate)
          ) : fillStateFiltered ? (
            /*
             * ⚠️ [amended C3c.1] SUPPRESSED WITH THE COUNT AND THE STATUS, FOR THE SAME REASON.
             *
             * `dutyDateFrom`/`dutyDateTo` are `$min`/`$max` over the SAME post-filter `$group`, so
             * a filtered header's span describes only the surviving children: an order running
             * 15–21 August with one untouched night on the 21st reads "21 Aug" under Status = Not
             * named. Leaving it while dashing the other two would have been the worse outcome — a
             * row that visibly withholds two numbers and quietly states a wrong third.
             */
            <span className="text-muted-foreground" title={INBOX_COPY.filteredHeader}>
              {INBOX_COPY.dash}
            </span>
          ) : (
            /* ⚠️ THE SPAN WITHIN THE WINDOW, never the arrangement's whole life. */
            INBOX_COPY.groupSpan(row.dutyDateFrom, row.dutyDateTo)
          )}
        </span>
      ),
    },
    {
      title: INBOX_COPY.columns.time,
      key: 'startsAt',
      render: (_value, row) =>
        row.rowKind === 'duty' ? (
          <span className="whitespace-nowrap">
            {row.timeLabel}
            {row.spansMidnight ? <span className="ml-1 text-xs text-muted-foreground">+1 day</span> : null}
          </span>
        ) : null,
    },
    {
      title: INBOX_COPY.columns.progress,
      key: 'progress',
      render: (_value, row) =>
        row.rowKind === 'group' && fillStateFiltered ? (
          /* ⚠️ A DASH, NOT A BLANK. A blank cell reads as "nothing asked for"; the dash plus the
             `title` says the number was withheld and why. See `fillStateFiltered`. */
          <span className="text-muted-foreground" title={INBOX_COPY.filteredHeader}>
            {INBOX_COPY.dash}
          </span>
        ) : (
          /* ⚠️ [C5b review] `namedOf`, THE ONE SPELLING — this column read `8 of 12` where every other
             surface stating the same fact reads `8 of 12 named` (D10). */
          <span className="tabular-nums">{namedOf(row.namedCount, row.allocatedCount)}</span>
        ),
    },
    {
      title: INBOX_COPY.columns.status,
      key: 'fillState',
      sorter: true,
      render: (_value, row) =>
        row.rowKind === 'group' && fillStateFiltered ? (
          <span className="text-muted-foreground" title={INBOX_COPY.filteredHeader}>
            {INBOX_COPY.dash}
          </span>
        ) : (
          <span className="inline-flex flex-col gap-0.5">
            <span className="inline-flex items-center gap-1.5">
              {/* ⚠️ [C5b §3] `fillBadge`, reading `closedShort` — a share closed short at nobody named
                  read `0 of 12 · All Filled 🔒` here, because the DTO carried no flag (D4). */}
              <StatusDot tone={fillBadge(row).tone} label={fillBadge(row).label} />
              {/* ⚠️ Green WITH A LOCK is a distinct state, and the glyph never travels alone. */}
              {row.rowKind === 'duty' && row.completedAt !== null ? (
                <Lock className="size-3.5 text-muted-foreground" aria-hidden />
              ) : null}
            </span>
            {/*
              ⚠️ [P7A §4] NOT ASSIGNED IS NOT SHORT — THE REASON UNDER `Closed short`, ON THE DUTY ROW ONLY.
              The server sends words only where the row is closed short and asked for somebody, so a
              row nobody has answered carries none. A muted line that wraps and is never truncated —
              and a span rather than a `Tooltip`, so the overlay census does not move.
            */}
            {row.rowKind === 'duty' && row.shortfallReasons.length > 0 ? (
              <span data-testid="inbox-row-reason" className="max-w-64 text-xs break-words text-muted-foreground">
                {INBOX_COPY.closedShortReason(row.shortfallReasons)}
              </span>
            ) : null}
          </span>
        ),
    },
  ];

  /*
   * ⚠️ [C3c §2f] `lockFirst: false`, AND THIS IS THE EASIEST THING IN THE PASS TO MISS.
   *
   * `columnKeySet` locks the FIRST column because it opens the row. `HierarchyTable` INJECTS the
   * tree column ahead of these, so index 0 here is no longer the row's first column — leaving the
   * default would lock `Asked of` for no reason, and nothing would fail. `pages/OrgTree` is the
   * precedent and `lib/tableColumns.ts` already documents it.
   */
  const columnKeys = columnKeySet(allColumns, { lockFirst: false });
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'inbox' });
  const columns = allColumns.filter((column) => columnVisibility.isVisible(String(column.key)));

  /*
   * ⚠️ [C3c §2b] COLLAPSED BY DEFAULT — "expanding reveals duty point rows" is §2b's own wording,
   * and it keeps a page at twenty rendered rows rather than up to eight hundred.
   */
  const expansion = useTreeExpansion({ expandableKeys: [], autoKeys: [], boundedKeys: [] });

  return (
    <div className="flex flex-col gap-6">
      {/*
       * ⚠️ NO SUBTITLE, BY DECISION — the header is the title, the icon and the filters.
       *
       * It said two things and both survive. The COUNT is the table's own — `useServerTable`
       * renders a total in the pager, so a sentence repeating it above was saying the same
       * thing twice. The DEFAULT WINDOW is on the date pickers, which read `Today` and
       * `In 7 days` while they are empty: the hint existed "so the default is never a
       * mystery", and the placeholders answer that without a line of prose.
       */}
      <PageHeader
        icon={InboxIcon}
        title={INBOX_COPY.title}
        filters={
          <>
            <FilterField label={INBOX_COPY.filters.from} className="w-40">
              <DatePicker
                format="dd LLL yyyy"
                placeholder={INBOX_COPY.fromPlaceholder}
                value={asPickerDate(table.params.filters.dateFrom)}
                onChange={(value) => table.setFilter('dateFrom', value?.toISODate() ?? undefined)}
              />
            </FilterField>
            <FilterField label={INBOX_COPY.filters.to} className="w-40">
              <DatePicker
                format="dd LLL yyyy"
                placeholder={INBOX_COPY.toPlaceholder}
                value={asPickerDate(table.params.filters.dateTo)}
                onChange={(value) => table.setFilter('dateTo', value?.toISODate() ?? undefined)}
              />
            </FilterField>
            {/*
              ⚠️ [C1d §2] TWO AXES, TWO CONTROLS — `pages/Arrangements`' rule, and the same
              `Select`. `Show` says whether a duty is in circulation; `Status` beside it says
              how far its division has got with naming. Collapsing them would put "Archived"
              beside "Not named" as though they were peers.

              ⚠️ AN ANTD `Select` RATHER THAN THIS PAGE'S NATIVE ONES, DELIBERATELY. The four
              other `show` controls in the app are this component, this width and these three
              words; the archive axis reading identically everywhere is worth more than one
              filter row being internally uniform. The native selects beside it are the
              outlier, and the tests that drive them with `selectOptions` are untouched.

              ⚠️ NO `allowClear`: "Current" is a VALUE, not the absence of a filter.
            */}
            <FilterField label={INBOX_COPY.filters.show} className="w-44">
              <Select
                className="w-full"
                value={show}
                onChange={(value: string) => table.setFilter('show', value === 'current' ? undefined : value)}
                options={[
                  { value: 'current', label: INBOX_COPY.showCurrent },
                  { value: 'archived', label: INBOX_COPY.showArchived },
                  { value: 'all', label: INBOX_COPY.showAll },
                ]}
                data-testid="show-filter"
              />
            </FilterField>
            {/*
              ⚠️ [C5c §7a] THE SEARCH, AFTER `Show` — where `pages/Arrangements` puts its own, so the two
              list screens an operator moves between read in one order. Its placeholder is the one
              sentence every order search carries (`lib/arrangementSearch.ts`).
            */}
            <FilterField label={INBOX_COPY.filters.search} className={ARRANGEMENT_SEARCH_FIELD_CLASS}>
              <Input.Search
                allowClear
                placeholder={INBOX_COPY.searchPlaceholder}
                value={table.searchInput}
                onChange={(event) => table.onSearchChange(event.target.value)}
              />
            </FilterField>
            <FilterField label={INBOX_COPY.filters.status} className="w-44">
              <select
                className="h-[38px] rounded-md border border-input bg-background px-2 text-sm"
                aria-label={INBOX_COPY.filters.status}
                value={table.params.filters.fillState ?? ''}
                onChange={(event) => table.setFilter('fillState', event.target.value || undefined)}
              >
                <option value="">{INBOX_COPY.anyStatus}</option>
                {/* ⚠️ [C5b §3] `COMPLETE` finds both of its causes, and its option says so. */}
                {FILL_STATES.map((state: FillState) => (
                  <option key={state} value={state}>
                    {fillFilterLabel(state)}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField label={INBOX_COPY.filters.policy} className="w-48">
              <select
                className="h-[38px] rounded-md border border-input bg-background px-2 text-sm"
                aria-label={INBOX_COPY.filters.policy}
                value={table.params.filters.policyId ?? ''}
                onChange={(event) => table.setFilter('policyId', event.target.value || undefined)}
              >
                <option value="">{INBOX_COPY.anyPolicy}</option>
                {(policies.data?.items ?? []).map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    {policy.name}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField label={INBOX_COPY.filters.division} className="w-56">
              <OrgUnitPicker
                allowClear
                placeholder={INBOX_COPY.anyDivision}
                value={table.params.filters.divisionOrgUnitId}
                onChange={(value) => table.setFilter('divisionOrgUnitId', value)}
              />
            </FilterField>
            {/* The Columns menu sits IN the filter row, not in `actions`: it is a view control,
                the same argument `pages/Arrangements` makes for its Segmented. */}
            <ColumnsMenu
              className="ml-auto"
              columns={allColumns ?? []}
              visibility={columnVisibility}
              locked={columnKeys.locked}
            />
          </>
        }
      />

      {/*
        ─────────────────────────────────────────────────────────────────────────────────
        ⚠️ [C3c §2f] THE EXISTING HIERARCHY TABLE, NOT A SECOND ONE.

        ⚠️ §2c — CLICKING THE ARRANGEMENT NAME ONLY EXPANDS OR COLLAPSES, and that is this
        repository's own rule rather than a new one. CLAUDE.md §8 records that `HierarchyTable`
        deliberately does NOT follow first-column-opens-the-row: "a tree row is already the
        control, with `onSelect`/`onOpen` and a roving `tabIndex`, so a link nested inside its
        cell would add a second tab stop per row and give `Enter` two meanings."

        ⚠️ ARROW-KEY NAVIGATION STOPS AT THE PAGE BOUNDARY. `flattenVisible` walks the rows it is
        given, which is now one page. Honest, accepted, and said out loud rather than discovered.
        ─────────────────────────────────────────────────────────────────────────────────
      */}
      <HierarchyTable<InboxTreeRow>
        rows={table.tableProps.dataSource ?? []}
        loading={table.tableProps.loading ?? false}
        pagination={table.tableProps.pagination}
        onChange={table.tableProps.onChange}
        expansion={expansion}
        columns={columns}
        ariaLabel={INBOX_COPY.title}
        data-testid="inbox-tree"
        tree={{
          title: INBOX_COPY.columns.arrangement,
          /*
           * ⚠️ [C3c.2] 26rem → 34rem, ON THE CLIENT'S OWN INSTRUCTION, so the header row is one
           * line. The chip, the title and the preview eye were being squeezed into a measure that
           * a real PA number plus a real procession title does not fit in. Widening is half the
           * fix; the other half is `HierarchyTable` narrowing beside the chevron instead of
           * dropping under it, and the third is that most orders now carry no chip at all.
           */
          width: '34rem',
          /*
           * ⚠️ [C3c.2] THE PA ORDER NUMBER ONLY — `reference` IS NO LONGER A FALLBACK HERE.
           *
           * `paNumber` is what is typed on the paper order; `reference` is the id this system
           * MINTS (`PA-2026-0047`). Falling back put a number in the chip that appears on no
           * document an operator holds, in the same grey lozenge and the same monospace as a real
           * one — so the row asserted an order number that nobody could match against paper.
           * `null` renders no chip, which is honest: this order has no PA number yet.
           *
           * ⚠️ THIS IS A DELIBERATE DIVERGENCE FROM THE OTHER SURFACES, NOT AN OVERSIGHT.
           * [C2a §5b] gives the fill screen `paNumber ?? reference` and `dashboardFill.service.ts`
           * does the same, because there the chip is the ONLY thing naming which order is open and
           * something must be shown. Here the title is beside it and the row is one of many, so an
           * absent chip costs nothing and a wrong-looking one costs a misidentified order.
           * [C2b §8b]'s printed sheet — which shows BOTH and never collapses them — is the other
           * end of the same argument.
           */
          code: (row) => (row.rowKind === 'group' ? row.paNumber : `DP${String(row.dutyPointOrdinal)}`),
          /*
           * ⚠️ [amended C3c.1] THE CAP NOTICE IS A **BADGE**, NOT PART OF THE NAME — AND IT WAS
           * PART OF THE NAME, WHERE IT WAS INVISIBLE.
           *
           * `HierarchyTable` renders `name` inside a `truncate` box in a fixed-width column, so
           * anything appended after the title is the first thing the ellipsis eats: for any order
           * with an ordinary title the notice simply did not appear. It is the one thing on that
           * row an operator must not miss — it says work is missing from the list — so it renders
           * in the slot outside the clip.
           *
           * `dutyRowCount` is the TRUE child count and `children.length` is what the server
           * actually sent, so it appears only where they differ.
           *
           * ⚠️ [amended C5c §10] AND THE RAISER PILL JOINS IT, FIRST, IN THE SAME SLOT — for the same
           * reason: appended to the name it would be the first thing the ellipsis eats. The two must be
           * able to show together (a busy HQ order is the ordinary case), so they share one flex row
           * rather than one replacing the other. A duty row carries neither: the pill is a fact about
           * the ORDER, which is what a header is. [amended P7A §2] Every group has a level now —
           * `HQ` · `DRO` · `Unit` — and only a `null` one (an unresolvable raiser) draws no pill.
           */
          badge: (row) => {
            if (row.rowKind !== 'group') return null;
            const capped = row.children.length < row.dutyRowCount;
            if (row.raisedAtLevel === null && !capped) return null;
            return (
              <span className="inline-flex items-center gap-1.5">
                <RaiserPill level={row.raisedAtLevel} />
                {capped ? (
                  <span
                    data-testid="inbox-rows-capped"
                    className="whitespace-nowrap rounded bg-secondary px-1.5 py-0.5 text-xs text-muted-foreground"
                  >
                    {INBOX_COPY.dutyRowsCapped(row.children.length, row.dutyRowCount)}
                  </span>
                ) : null}
              </span>
            );
          },
          name: (row) =>
            row.rowKind === 'group' ? (
              /*
               * ⚠️ §2c — TEXT, NOT A LINK. Every action lives on a duty point row.
               *
               * ⚠️ [C1e §2f] AND THE PREVIEW EYE MOVED UP HERE WITH IT. It previews the ORDER,
               * which is what a header IS — and one eye per group rather than one per duty row
               * is [PRE-P6.1]'s measured overlay-per-row cost going the right way. It does not
               * break `NameCell`'s one-destination rule: the name reaches nothing at all now.
               */
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <span title={row.displayTitle}>{truncateName(row.displayTitle)}</span>
                {row.arrangementVisible ? (
                  <PreviewEye
                    eyeKey={row.key}
                    title={row.displayTitle}
                    onOpen={() => setPreview({ id: row.arrangementId, eyeKey: row.key })}
                  />
                ) : null}
              </span>
            ) : (
              /*
               * ⚠️ A REAL `<a>`, so ⌘-click and copy-link work. And ⚠️ [D4.5 §3e] `?division=`
               * still rides on it: two duty rows can name the SAME deployment, so without the
               * parameter a super admin's `Mark complete` would close a division they were not
               * looking at.
               */
              <a
                className="hover:underline"
                title={row.dutyPointTitle}
                href={`/duties/${row.deploymentId}?division=${row.divisionOrgUnitId}`}
              >
                {truncateName(row.dutyPointTitle)}
              </a>
            ),
        }}
        onSelect={(row) => {
          /* ⚠️ §2c — a header toggles; a duty row does nothing on a plain click. */
          if (row.rowKind === 'group') expansion.onExpand(!expansion.expandedSet.has(row.key), row.key);
        }}
        onOpen={(row) => {
          if (row.rowKind === 'group') {
            expansion.onExpand(!expansion.expandedSet.has(row.key), row.key);
            return;
          }
          void navigate(`/duties/${row.deploymentId}?division=${row.divisionOrgUnitId}`);
        }}
        empty={
          table.query.data?.searched ? (
            <EmptyState size="sm" title={INBOX_COPY.noMatchTitle} description={INBOX_COPY.noMatchBody} />
          ) : (
            <EmptyState size="sm" title={INBOX_COPY.emptyTitle} description={INBOX_COPY.emptyBody} />
          )
        }
      />

      {/* ⚠️ [C1e §2] ONE INSTANCE, AT THE PAGE. Radix renders nothing while closed. */}
      <ArrangementPreviewModal
        arrangementId={preview?.id ?? null}
        onClose={() => setPreview(null)}
        openedFrom={preview?.eyeKey ?? null}
      />
    </div>
  );
};

export default InboxPage;
